'use client';

import { useState, useRef, useEffect } from 'react';
import { Send, CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { submitContact } from '@/lib/api';

export default function ContactForm({ contact }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    // Honeypot anti-spam : champ cache que les bots remplissent
    website: '',
  });
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  // V1.25.1455 — Auto-redimensionnement de l'iframe : le listener est INTEGRE
  // dans ce composant (aucun script a coller). Si le contenu de l'iframe est
  // un formulaire Ligobox (PublicFormPage envoie sa hauteur via postMessage),
  // l'iframe epouse automatiquement la hauteur du formulaire -> plus de
  // contenu coupe. Pour une iframe externe (Tally, Typeform...) qui n'envoie
  // rien, la hauteur configuree reste utilisee.
  const iframeRef = useRef(null);
  const [autoHeight, setAutoHeight] = useState(null);
  useEffect(() => {
    const onMsg = (event) => {
      const ifr = iframeRef.current;
      if (!ifr) return;
      // Contrôle de provenance ROBUSTE (même stratégie que form-embed.js) : on
      // accepte le message si la SOURCE est notre iframe OU si l'ORIGINE est
      // celle du formulaire. La seule comparaison de source peut échouer (ex.
      // après un rechargement de l'iframe sur mobile) → sans ce repli, les
      // messages de redimensionnement ET de conversion étaient rejetés.
      let formOrigin = null;
      try { formOrigin = new URL(ifr.src).origin; } catch (_) {}
      if (event.source !== ifr.contentWindow && event.origin !== formOrigin) return;
      const d = event.data;
      if (d && d.type === 'ligobox-form-resize' && typeof d.height === 'number' && d.height > 0) {
        setAutoHeight(Math.ceil(d.height));
      }
      // CONVERSION (Google Tag Manager) : le formulaire est en mode iframe (module
      // « visite chantier » du tenant). L'iframe (PublicFormPage) envoie
      // `ligobox-form-submitted` a l'envoi reussi ; on relaie ici en poussant
      // `contact_form_submit` dans le dataLayer de la vitrine, ou GTM ecoute.
      if (d && d.type === 'ligobox-form-submitted') {
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'contact_form_submit' });
        } catch (_) { /* jamais bloquant */ }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot rempli = bot, on simule un succes sans envoyer
    if (form.website) {
      setStatus('success');
      return;
    }

    if (!form.name || !form.email || !form.message) {
      setErrorMsg('Merci de remplir nom, email et message.');
      setStatus('error');
      return;
    }

    setStatus('sending');
    setErrorMsg('');
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        company: form.company,
        phone: form.phone,
        message: form.message,
      });
      setStatus('success');
      setForm({ name: '', email: '', company: '', phone: '', message: '', website: '' });
    } catch (err) {
      setErrorMsg(err.message || 'Erreur lors de l\'envoi. Reessayez plus tard.');
      setStatus('error');
    }
  };

  // Mode IFRAME : si une URL iframe est configurée, on remplace le formulaire
  // interne par une iframe (Tally, Typeform, Google Forms…). Hauteurs desktop
  // et mobile configurables, fond transparent en option.
  const iframeUrl = contact?.iframe_url;
  const transparentBg = !!contact?.transparent_bg;
  const sectionBg = transparentBg ? 'transparent' : '#fff';

  if (iframeUrl) {
    const hDesk = parseInt(contact?.iframe_height_desktop, 10) || 700;
    const hMob  = parseInt(contact?.iframe_height_mobile, 10) || 900;
    return (
      <section id="contact" style={{ padding: '44px 0', background: sectionBg }}>
        <div className="container-page">
          {(contact?.title || contact?.subtitle) && (
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              {contact?.title && (
                <h2 className="section-title" style={{ marginBottom: 12 }}>{contact.title}</h2>
              )}
              {contact?.subtitle && (
                <p style={{ fontSize: 17, color: 'var(--color-text-muted)', maxWidth: 640, margin: '0 auto' }}>
                  {contact.subtitle}
                </p>
              )}
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            title={contact?.title || 'Formulaire de contact'}
            className="contact-iframe"
            scrolling={autoHeight ? 'no' : 'auto'}
            style={{
              width: '100%', border: 'none', display: 'block',
              background: transparentBg ? 'transparent' : '#fff',
              // V1.25.1455 — hauteur auto si le formulaire Ligobox renvoie sa
              // hauteur ; sinon hauteur configuree (desktop / mobile).
              height: (autoHeight || hDesk) + 'px',
              // V1.25.946 — La hauteur mobile passe par une CSS variable lue
              // dans globals.css (.contact-iframe @media). Plus de <style>
              // inline qui causait un mismatch d'hydratation React #329.
              '--contact-height-mobile': (autoHeight || hMob) + 'px',
            }}
            allow="clipboard-write; camera; microphone"
          />
        </div>
      </section>
    );
  }

  return (
    <section id="contact" style={{ padding: '44px 0', background: sectionBg }}>
      <div className="container-page" style={{ maxWidth: '720px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <span style={{
            display: 'inline-block',
            padding: '6px 14px',
            borderRadius: '999px',
            background: 'rgba(226, 114, 40, 0.1)',
            color: 'var(--color-primary)',
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '16px',
          }}>
            CONTACT
          </span>
          {contact?.title && (
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              {contact.title}
            </h2>
          )}
          {contact?.subtitle && (
            <p className="section-subtitle">
              {contact.subtitle}
            </p>
          )}
        </div>

        {status === 'success' ? (
          <div style={{
            padding: '40px',
            background: 'var(--color-bg-alt)',
            border: '2px solid var(--color-primary)',
            borderRadius: '16px',
            textAlign: 'center',
          }}>
            <CheckCircle2 size={48} color="var(--color-primary)" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 8px' }}>
              Message envoye !
            </h3>
            <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              Nous revenons vers vous tres vite.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{
            background: 'var(--color-bg-alt)',
            padding: '40px',
            borderRadius: '16px',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }} className="form-grid">
              <Field label="Nom" name="name" value={form.name} onChange={handleChange} required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
              <Field label="Entreprise" name="company" value={form.company} onChange={handleChange} />
              <Field label="Telephone" name="phone" type="tel" value={form.phone} onChange={handleChange} />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Field
                label="Message"
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                multiline
                rows={5}
              />
            </div>

            {/* Honeypot : invisible, anti-spam */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {status === 'error' && (
              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: '8px',
                color: '#c00',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <AlertCircle size={18} />
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="btn-primary"
              style={{
                marginTop: '24px',
                width: '100%',
                justifyContent: 'center',
                opacity: status === 'sending' ? 0.6 : 1,
                cursor: status === 'sending' ? 'not-allowed' : 'pointer',
              }}
            >
              {status === 'sending' ? 'Envoi...' : <>Envoyer <Send size={16} /></>}
            </button>

            {contact?.email_destinataire && (
              <p style={{
                textAlign: 'center',
                marginTop: '20px',
                fontSize: '14px',
                color: 'var(--color-text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                justifyContent: 'center',
              }}>
                <Mail size={14} />
                Ou ecrivez directement a <a href={`mailto:${contact.email_destinataire}`} style={{ color: 'var(--color-primary)', fontWeight: 500 }}>{contact.email_destinataire}</a>
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, name, type = 'text', value, onChange, required, multiline, rows }) {
  const baseStyle = {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    fontSize: '15px',
    fontFamily: 'inherit',
    background: '#fff',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    outline: 'none',
  };

  return (
    <label style={{ display: 'block' }}>
      <span style={{
        display: 'block',
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--color-text)',
        marginBottom: '6px',
      }}>
        {label} {required && <span style={{ color: 'var(--color-primary)' }}>*</span>}
      </span>
      {multiline ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={rows || 4}
          style={{ ...baseStyle, resize: 'vertical', minHeight: '120px' }}
          onFocus={e => { e.target.style.borderColor = 'var(--color-primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(226, 114, 40, 0.1)'; }}
          onBlur={e => { e.target.style.borderColor = 'var(--color-border)'; e.target.style.boxShadow = 'none'; }}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          style={baseStyle}
          onFocus={e => { e.target.style.borderColor = 'var(--color-primary)'; e.target.style.boxShadow = '0 0 0 3px rgba(226, 114, 40, 0.1)'; }}
          onBlur={e => { e.target.style.borderColor = 'var(--color-border)'; e.target.style.boxShadow = 'none'; }}
        />
      )}
    </label>
  );
}
